import {useMemo, useState} from "react";
import {BaseAlert, BaseBreadcrumb, BaseButton, BaseInput} from "@/components";
import GameCard from "./components/GameCard";
import type {Game} from "./games.types";
import {useGames} from "./useGames";

export interface GamesListPageProps {
  onGameSelect?: (game: Game) => void;
}

const GamesListPage = ({onGameSelect}: GamesListPageProps) => {
  const {games, isLoading, isError, error, refetch} = useGames();
  const [search, setSearch] = useState("");

  const filteredGames = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return games;
    return games.filter(game => game.name.toLowerCase().includes(term) || game.provider.toLowerCase().includes(term));
  }, [games, search]);

  return (
    <section className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 p-6">
      <BaseBreadcrumb items={[{label: "Juegos"}]} />
      <header className="flex flex-col gap-2">
        <h1 className="m-0 text-[var(--font-h1)] font-bold text-[var(--color-text)]">Juegos</h1>
        <p className="m-0 text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Explora nuestra colección de juegos más recientes
        </p>
      </header>

      <BaseInput
        type="search"
        placeholder="Buscar juego o proveedor…"
        value={search}
        onChange={event => setSearch(event.target.value)}
        fullWidth
        aria-label="Buscar juegos"
      />

      {isLoading && <p className="m-0 text-center text-[var(--color-text-secondary)]">Cargando juegos…</p>}

      {isError && (
        <div className="flex flex-col gap-3">
          <BaseAlert
            variant="danger"
            title="No pudimos cargar los juegos"
          >
            {error?.message ?? "Inténtalo de nuevo en unos segundos."}
          </BaseAlert>
          <BaseButton
            variant="secondary"
            onClick={refetch}
          >
            Reintentar
          </BaseButton>
        </div>
      )}

      {!isLoading && !isError && filteredGames.length === 0 && (
        <p className="m-0 py-10 text-center text-[var(--color-text-tertiary)]">
          {search ? "Sin resultados para tu búsqueda" : "Aún no hay juegos disponibles"}
        </p>
      )}

      {filteredGames.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filteredGames.map(game => (
            <GameCard
              key={`${game.provider}-${game.id}`}
              game={game}
              onClick={onGameSelect}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default GamesListPage;
